/**
 * 创作任务稳定错误码（与后端 {@link CreationTaskFailureKind} 同步）。
 */
export const CreationTaskErrorCode = {
  UPSTREAM_FAILED: 'CREATION_UPSTREAM_FAILED',
  RATE_LIMITED: 'CREATION_RATE_LIMITED',
  RESULT_PERSIST_FAILED: 'CREATION_RESULT_PERSIST_FAILED',
  RESULT_REMOTE_FETCH_FAILED: 'CREATION_RESULT_REMOTE_FETCH_FAILED',
  RESULT_TOO_LARGE: 'CREATION_RESULT_TOO_LARGE',
  RESULT_SOURCE_NOT_FOUND: 'CREATION_RESULT_SOURCE_NOT_FOUND',
  RESULT_REGISTER_FAILED: 'CREATION_RESULT_REGISTER_FAILED',
  RESULT_STORAGE_FAILED: 'CREATION_RESULT_STORAGE_FAILED',
  WORKER_UNEXPECTED: 'CREATION_WORKER_UNEXPECTED',
} as const

export type CreationTaskErrorCode =
  (typeof CreationTaskErrorCode)[keyof typeof CreationTaskErrorCode]

export type CreationTaskFailureView = {
  errorCode?: string | null
  errorMessage?: string | null
  reconcilable?: boolean | null
}

const RESULT_PREFIX = 'CREATION_RESULT_'

/** 是否落库类失败（优先 errorCode，过渡期回退 message 子串）。 */
export function isCreationResultPersistFailure(task: CreationTaskFailureView): boolean {
  if (task.reconcilable === true) return true
  const code = task.errorCode?.trim()
  if (code?.startsWith(RESULT_PREFIX)) return true
  return isLegacyResultPersistMessage(task.errorMessage)
}

/** 是否可展示「重试落库」：后端 reconcilable 为权威，否则启发式回退。 */
export function canRetryCreationPersist(task: CreationTaskFailureView & { status?: string }): boolean {
  if (task.reconcilable != null) return task.reconcilable
  if (task.status !== 'FAILED' && task.status !== 'UNAVAILABLE') return false
  return isCreationResultPersistFailure(task)
}

function isLegacyResultPersistMessage(message: string | null | undefined): boolean {
  const m = message?.trim() ?? ''
  if (!m) return false
  return (
    m.includes('落库失败') ||
    m.includes('生成图登记失败') ||
    m.includes('生成图存储失败') ||
    m.includes('无法下载远程图片') ||
    m.includes('图片过大') ||
    m.includes('存储登记不完整') ||
    m.includes('未正确落库') ||
    m.includes('图片不存在或已删除')
  )
}

export function creationTaskErrorDefaultMessage(code: string | null | undefined): string {
  switch (code?.trim()) {
    case CreationTaskErrorCode.UPSTREAM_FAILED:
      return '上游生图失败'
    case CreationTaskErrorCode.RATE_LIMITED:
      return '请求过于频繁，请稍后再试'
    case CreationTaskErrorCode.RESULT_PERSIST_FAILED:
      return '生成图落库失败'
    case CreationTaskErrorCode.RESULT_REMOTE_FETCH_FAILED:
      return '无法下载远程结果图'
    case CreationTaskErrorCode.RESULT_TOO_LARGE:
      return '结果图过大'
    case CreationTaskErrorCode.RESULT_SOURCE_NOT_FOUND:
      return '结果图源不存在或已删除'
    case CreationTaskErrorCode.RESULT_REGISTER_FAILED:
      return '生成图登记失败'
    case CreationTaskErrorCode.RESULT_STORAGE_FAILED:
      return '生成图存储失败'
    case CreationTaskErrorCode.WORKER_UNEXPECTED:
      return '任务处理异常'
    default:
      return '创作任务失败'
  }
}
